/**
 * Första-parts kampanjtouch för nyhetsbrev — utan mottagar-e-post i URL/storage.
 * URL-parametrar (nl_c / utm) sparas i sessionStorage oberoende av analytics-cookie
 * (samma policy som partner-lead), men beteendeevents (page_view m.m.) kräver
 * fortfarande analytics-samtycke via siteAnalytics.
 */

import {
  NEWSLETTER_ATTRIBUTION_WINDOW_MS,
} from './newsletterLinks';

export const NEWSLETTER_TOUCH_STORAGE_KEY = 'newsletterCampaignTouch';

export type NewsletterCampaignTouch = {
  campaignId: string;
  linkKey: string | null;
  utmCampaign: string | null;
  capturedAt: string;
};

export type NewsletterAttributionSlice = {
  campaign_id: string;
  link_key: string | null;
  utm_campaign: string | null;
  captured_at: string;
  /** Direkt från kampanjlänk i session — inte uppskattad. */
  attribution_quality: 'verified_direct';
  /** Modell: senaste nyhetsbrevs-touch inom fönstret. */
  model: 'last_newsletter_touch';
  window_days: number;
};

export function parseNewsletterTouchFromSearch(search: string): NewsletterCampaignTouch | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const nlC = (params.get('nl_c') || '').trim();
  const utmSource = (params.get('utm_source') || '').trim().toLowerCase();
  const utmMedium = (params.get('utm_medium') || '').trim().toLowerCase();
  const utmCampaign = (params.get('utm_campaign') || '').trim() || null;
  const linkKey = (params.get('nl_l') || params.get('utm_content') || '').trim() || null;

  const isNewsletter =
    Boolean(nlC) ||
    utmSource === 'newsletter' ||
    (utmMedium === 'email' && Boolean(utmCampaign) && utmSource === 'newsletter');

  if (!isNewsletter) return null;

  const campaignId = nlC || utmCampaign;
  if (!campaignId) return null;

  return {
    campaignId: campaignId.slice(0, 80),
    linkKey: linkKey ? linkKey.slice(0, 64) : null,
    utmCampaign,
    capturedAt: new Date().toISOString(),
  };
}

export function readNewsletterTouch(): NewsletterCampaignTouch | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(NEWSLETTER_TOUCH_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as NewsletterCampaignTouch;
  } catch {
    return null;
  }
}

export function writeNewsletterTouch(touch: NewsletterCampaignTouch): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(NEWSLETTER_TOUCH_STORAGE_KEY, JSON.stringify(touch));
}

/** Senaste touch inom attribution-fönstret, annars null. */
export function getActiveNewsletterTouch(
  nowMs = Date.now(),
  windowMs = NEWSLETTER_ATTRIBUTION_WINDOW_MS,
): NewsletterCampaignTouch | null {
  const touch = readNewsletterTouch();
  if (!touch?.campaignId || !touch.capturedAt) return null;
  const age = nowMs - new Date(touch.capturedAt).getTime();
  if (!Number.isFinite(age) || age < 0 || age > windowMs) return null;
  return touch;
}

/**
 * Fånga nl_* och utm från URL. Skriver alltid om nyhetsbrevslänk träffas
 * (senaste touch vinner — last_newsletter_touch).
 */
export function captureNewsletterTouchFromWindow(): NewsletterCampaignTouch | null {
  if (typeof window === 'undefined') return null;
  const parsed = parseNewsletterTouchFromSearch(window.location.search || '');
  if (!parsed) return getActiveNewsletterTouch();
  writeNewsletterTouch(parsed);
  return parsed;
}

export function newsletterTouchToAttributionSlice(
  touch: NewsletterCampaignTouch,
): NewsletterAttributionSlice {
  return {
    campaign_id: touch.campaignId,
    link_key: touch.linkKey,
    utm_campaign: touch.utmCampaign,
    captured_at: touch.capturedAt,
    attribution_quality: 'verified_direct',
    model: 'last_newsletter_touch',
    window_days: Math.round(NEWSLETTER_ATTRIBUTION_WINDOW_MS / (24 * 60 * 60 * 1000)),
  };
}

/** Matcha sparad attribution mot en kampanj-id (uuid eller slug i utm_campaign). */
export function attributionMatchesCampaign(
  snapshot: unknown,
  campaignId: string,
): { matched: boolean; quality: 'verified_direct' | 'estimated' | null; linkKey: string | null } {
  if (!snapshot || typeof snapshot !== 'object') {
    return { matched: false, quality: null, linkKey: null };
  }
  const s = snapshot as Record<string, unknown>;
  const nl = s.newsletter as Record<string, unknown> | undefined;
  if (nl && String(nl.campaign_id || '') === campaignId) {
    const q = nl.attribution_quality === 'estimated' ? 'estimated' : 'verified_direct';
    return {
      matched: true,
      quality: q,
      linkKey: nl.link_key != null ? String(nl.link_key) : null,
    };
  }

  // Uppskattad: UTM i first/last/conversion utan explicit newsletter-block
  const touches = [s.conversion, s.last_non_direct, s.first, s.partner].filter(Boolean) as Array<
    Record<string, unknown>
  >;
  for (const t of touches) {
    const utmSource = String(t.utmSource || t.utm_source || t.source || '').toLowerCase();
    const utmCampaign = String(t.utmCampaign || t.utm_campaign || t.campaign || '');
    const channel = String(t.channel || '').toLowerCase();
    if (
      (utmSource === 'newsletter' || channel === 'email') &&
      (utmCampaign === campaignId || utmCampaign.includes(campaignId.slice(0, 8)))
    ) {
      return {
        matched: true,
        quality: 'estimated',
        linkKey: t.utmContent || t.utm_content || t.link_key
          ? String(t.utmContent || t.utm_content || t.link_key)
          : null,
      };
    }
  }
  return { matched: false, quality: null, linkKey: null };
}
